import React, { useEffect, useState } from "react";
import { useError } from "../../../contexts/ErrorContext";
import { useQuery } from "@tanstack/react-query";

import useApi from "../../../hooks/useGaneshApi";

import { Box, Grid, Paper, Typography } from "@mui/material";
import GaneshCopyToClipboardBtn from "../../../components/UI/GaneshCopyToClipboard";
import "../BrandMetadataReview.css";
import BrandReviewInstructions from "../UI/BrandReviewInstructions";

import { formatDateHumanReadable } from "utils/formatting";

interface PlacesOmniReviewProps {
  omni_key: string;
}

interface OmniHealthStatus {
  omni_key: string;
  omni_first_observation: Date;
  omni_last_observation: Date;
  days_since_last_observation: string;
  health_status: string;
}

// Helper function to calculate gradient color for days since last observation
const getGradientColor = (days: number) => {
  if (days <= 10) return "green";
  if (days <= 25) return "orange";
  if (days <= 50) return "red";
  return "darkred";
};

// Helper function to determine health status color
const getHealthStatusColor = (status: string) =>
  status && status.toLowerCase() === "pass" ? "green" : "red";

const ReviewPlacesOmniHealth: React.FC<PlacesOmniReviewProps> = ({
  omni_key,
}) => {
  const { showError } = useError();
  const { apiRequest } = useApi();

  // **Fetch Countries using Dependent Query**
  const { data: omniHealthStatus, isLoading: loading } =
    useQuery<OmniHealthStatus>({
      queryKey: ["omniHealth", omni_key],
      queryFn: async () => {
        try {
          const response = await apiRequest<OmniHealthStatus | null>(
            `/brand_reports/omni_health_status/${omni_key}`,
            { method: "GET" }
          );
          if (response.data) {
            return response.data;
          } else {
            throw new Error("No data found");
          }
        } catch (error) {
          if (error instanceof Error) {
            showError(`Failed to fetch omni health status for ${omni_key}.`);
          } else {
            showError("Failed to fetch omni health status: Unknown error");
          }
          throw new Error("No data found");
        }
      },
      enabled: !!omni_key, // Only fetch when an omni is selected
    });

  const user_instructions = {
    section_id: "places-omni-health",
    instruction_title: "Omniscraper Health Check",
    task_description: "Review whether omnis are functioning as expected.",
  };

  return (
    <Box className="metadata-review-container">
      <BrandReviewInstructions {...user_instructions} />
      {loading ? (
        <Typography variant="body1">Loading...</Typography>
      ) : omniHealthStatus ? (
        Object.values(omniHealthStatus).some((value) => value) ? (
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {/* Omni Key */}
            {omniHealthStatus.omni_key && (
              <Grid item xs={12} sm={6} md={4}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "black", mb: 1 }}
                  >
                    OMNI KEY
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      overflow: "hidden",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        mr: 1,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {omniHealthStatus.omni_key}
                    </Typography>
                    <GaneshCopyToClipboardBtn
                      textToCopy={omniHealthStatus.omni_key}
                      buttonText=""
                      showButtonText={false}
                      alertLabel="Omni Key copied to clipboard!"
                      size="small"
                    />
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* Omni First Observation */}
            {omniHealthStatus.omni_first_observation && (
              <Grid item xs={12} sm={6} md={4}>
                <Paper elevation={2} style={{ padding: "16px" }}>
                  <Typography
                    variant="subtitle2"
                    style={{ color: "black", marginBottom: "8px" }}
                  >
                    OMNI FIRST OBSERVATION
                  </Typography>
                  <Typography variant="h6" style={{ fontWeight: "bold" }}>
                    {formatDateHumanReadable(
                      omniHealthStatus.omni_first_observation
                    )}
                  </Typography>
                </Paper>
              </Grid>
            )}

            {/* Omni Last Observation */}
            {omniHealthStatus.omni_last_observation && (
              <Grid item xs={12} sm={6} md={4}>
                <Paper elevation={2} style={{ padding: "16px" }}>
                  <Typography
                    variant="subtitle2"
                    style={{ color: "black", marginBottom: "8px" }}
                  >
                    OMNI LAST OBSERVATION
                  </Typography>
                  <Typography variant="h6" style={{ fontWeight: "bold" }}>
                    {formatDateHumanReadable(
                      omniHealthStatus.omni_last_observation
                    )}
                  </Typography>
                </Paper>
              </Grid>
            )}

            {/* Days Since Last Observation */}
            {omniHealthStatus.days_since_last_observation && (
              <Grid item xs={12} sm={6} md={4}>
                <Paper elevation={2} style={{ padding: "16px" }}>
                  <Typography
                    variant="subtitle2"
                    style={{ color: "black", marginBottom: "8px" }}
                  >
                    DAYS SINCE LAST OBSERVATION
                  </Typography>
                  <Typography
                    variant="h6"
                    style={{
                      fontWeight: "bold",
                      color: getGradientColor(
                        parseInt(
                          omniHealthStatus.days_since_last_observation,
                          10
                        )
                      ),
                    }}
                  >
                    {omniHealthStatus.days_since_last_observation}
                  </Typography>
                </Paper>
              </Grid>
            )}

            {/* Health Status */}
            {omniHealthStatus.health_status && (
              <Grid item xs={12} sm={6} md={4}>
                <Paper elevation={2} style={{ padding: "16px" }}>
                  <Typography
                    variant="subtitle2"
                    style={{ color: "black", marginBottom: "8px" }}
                  >
                    HEALTH STATUS
                  </Typography>
                  <Typography
                    variant="h6"
                    style={{
                      fontWeight: "bold",
                      color: getHealthStatusColor(
                        omniHealthStatus.health_status
                      ),
                    }}
                  >
                    {omniHealthStatus.health_status.toUpperCase()}
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        ) : (
          <Typography
            variant="body1"
            style={{
              color: "orange",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              backgroundColor: "rgba(255, 165, 0, 0.1)", // light orange background
              padding: "8px",
              borderRadius: "4px",
              marginTop: 25,
            }}
          >
            No Omni Health Status values available.
          </Typography>
        )
      ) : (
        <Typography variant="body1">No data available.</Typography>
      )}
    </Box>
  );
};

export default ReviewPlacesOmniHealth;
